import { LenderConfiguration } from '@shared/model/lender-config.model';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TokenService, OidSessionService } from '@cudirect/shared-ui-library';
import { Subject, Subscription } from 'rxjs';
import { ApiCacheService } from 'src/postapp/services/api.cache.service';
import { User_api } from 'src/user/constant/user-const';
import { LocationStrategy } from '@angular/common';
import { StatefulService } from '@app/service/stateful.service';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { StorageWrapperService } from '@workflow/ecp-shared/service/storage-wrapper/storage-wrapper.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { Info, RequestHeaderConst } from '@workflow/ecp-shared/constant/information.const';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { WorkflowLoaderService } from './workflow-loader.service';

/**
 * This is the new user session timeout threshold
 * There is not a need for this to be lender specific
 */
const USER_SESSION_TIMEOUT = 900; // 15 mins in SECONDS.

// const USER_SESSION_TIMEOUT = 145; // for TESTING ONLY

/**
 * service to handle userSession
 *
 * class UserSessionService
 */
@Injectable({
  providedIn: 'root'
})
export class UserSessionService {
  public timerEvent: Subject<string> = new Subject<string>();
  /** Lender data from api */
  public lenderData: LenderConfiguration;
  public subscriptions = new Subscription();
  public canHookCurrentBundle = false;
  public logoutInProgress = false;
  public activityTimerHolder;
  public headerConsts = RequestHeaderConst;

  constructor(
    private readonly api: ApiService,
    private readonly router: Router,
    private readonly storageWrapperService: StorageWrapperService,
    private readonly userService: UserService,
    private readonly apiCacheService: ApiCacheService,
    private readonly locationStrategy: LocationStrategy,
    private readonly sessionService: OidSessionService,
    private readonly statefulService: StatefulService,
    private workflowService: WorkflowService,
    private tokenService: TokenService,
    private readonly workflowLoader: WorkflowLoaderService
  ) {
    this.activityTimerHolder = this.sessionService.inactivityTimer('ecp-user-session').subscribe((timerData) => {
      switch (timerData) {
        case 'warn':
          this.timerEvent.next(Info.show);
          break;
        case 'logout':
          this.logOut();
          break;
        default:
          console.log('Unknown msg from inactivityTimer: ', timerData);
      }
    });
    this.subscriptions.add(this.activityTimerHolder);
  }

  public get token(): string {
    return this.userService.token;
  }

  public userFirstName(): string {
    if (this.tUserFirstName) {
      return this.tUserFirstName;
    }

    if (this.userService.user?.firstName?.length) {
      this.storageWrapperService.set('user.fName', this.userService.user.firstName);
      this.storageWrapperService.set('user.lName', this.userService.user.lastName || '');
      this.tUserFirstName = this.userService.user.firstName;
      this.tUserLastName = this.userService.user.lastName;
    } else {
      this.tUserFirstName = this.storageWrapperService.get('user.fName');
      this.tUserLastName = this.storageWrapperService.get('user.lName');
    }
    return this.tUserFirstName;
  }
  private tUserFirstName: string;
  private tUserLastName: string;

  public userFullName(): string {
    return this.userFirstName() + ' ' + this.tUserLastName;
  }
  /**
   * Borrower Activity Timer
   */
  public startTimer(): void {
    this.sessionService.startActivityTimer(USER_SESSION_TIMEOUT, 'ecp-user-session');
    const token = this.tokenService.getCachedToken();
    if (token) {
      this.tokenService.updateToken(token);
      this.sessionService.startRefreshTimer('ecp-user-session');
    }
  }

  /**
   * Stops the activity timer
   */
  public stopTimer(): void {
    this.sessionService.stopActivityTimer('ecp-user-session');
    this.timerEvent.next(Info.hide);
  }

  public logOut(navigateToLogin = true, userInactive = false): void {
    this.stopTimer();
    this.statefulService.wrap('ss:authorization').value = '';

    // When a user logouts, theres no need to keep ANYTHING in session Storage
    // Probably add this to a Shared-UI session service
    // TODO: Won't this affect admin portal session storage as well? This seems like it should be surgical.

    this.sessionService.stopActivityTimer('ecp-user-session');

    // Manually adding the authorization header to the logout request
    const flowType = JSON.parse(sessionStorage.getItem('BMP.FlowType'));
    const storageBlob = JSON.parse(sessionStorage.getItem('cuASSD:ss'));
    let auth;
    if (flowType === 'OLA') {
      auth = storageBlob['auth-ola'];
    } else if (flowType === 'OAO') {
      auth = storageBlob.authorization;
    }
    let authObj = {};
    if (auth) {
      authObj = { defaultHeaders: { [RequestHeaderConst.authorization]: auth, [RequestHeaderConst.dxSessionId]: auth } };
    } else {
      authObj = {};
    }
    this.api
      .request<void>({ urlObj: { ...User_api.LogOut, ...authObj } })
      .toPromise()
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        this.tokenService.invalidateToken();
        this.userService.logout();
        sessionStorage.clear();
        this.apiCacheService.clear();
        this.workflowLoader.clear();
        this.workflowService.clearCurrentFlow();
        this.workflowService.clearAllInstance();
        this.sessionService.destroySession(true, 'ecp-user-session');
        this.logoutInProgress = true;
        this.logoutUser(navigateToLogin, userInactive);
      });
  }

  private logoutUser(navigateToLogin: boolean, userInactive: boolean): void {
    this.statefulService.wrap('ola:extraData').saveLock();
    if (navigateToLogin) {
      if (userInactive) {
        this.router.navigate(['/user/login'], { queryParams: { inactive: true } });
      } else {
        this.router.navigate(['/']);
      }
      history.pushState(null, null);
      this.locationStrategy.onPopState(() => {
        history.pushState(null, null);
      });
    }
    // this.logoutInProgress = false;
  }

  public refreshToken(callback?: (data?) => void, errorCallback?: (errorData?) => void): void {
    if (this.userService.token !== undefined) {
      this.api
        .request<void>({ urlObj: User_api.RefreshToken })
        .toPromise()
        .then((data?) => {
          if (callback) {
            callback(data);
          }
        })
        .catch((err) => {
          if (errorCallback) {
            errorCallback(err);
          }
        });
    }
  }

  public closeModal(): void {
    this.timerEvent.next(Info.hide);
    this.startTimer();
  }

  public sessionPollTimer(): void {
    const timeout = 1000;
    setInterval(() => {
      this.sessionPoll();
    }, timeout);
  }

  private sessionPoll(): void {
    let lastSessionToken = this.storageWrapperService.get<string>(Info.lastSessionToken);
    lastSessionToken = lastSessionToken === undefined ? '' : lastSessionToken;
    let currentToken = this.userService.token;
    currentToken = currentToken === undefined ? '' : currentToken;

    if (currentToken !== lastSessionToken && currentToken !== '' && lastSessionToken !== '') {
      location.reload();
    }

    this.storageWrapperService.set(Info.lastSessionToken, currentToken);
  }
}
